import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { log } from '@/lib/log';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      // 401 is expected for anonymous visitors; everything else worth logging
      if (err?.response?.status !== 401) log.warn('/auth/me failed', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, defer to AuthCallback
    // (it will exchange the session_id and establish the cookie first).
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    return res.data.user;
  };
  const register = async (email, password, name) => {
    const res = await api.post('/auth/register', { email, password, name });
    setUser(res.data.user);
    return res.data.user;
  };
  const completeOAuth = async (session_id) => {
    const res = await api.post('/auth/session', { session_id });
    setUser(res.data.user);
    return res.data.user;
  };
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      log.warn('logout call failed (continuing local logout)', err);
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, completeOAuth, refresh: checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx) || { user: null, loading: false };
}
